import { Theme } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';

import { DocumentDetailsViewOptions } from '@/modules/documents/documents.constants';

interface UseViewerHeightProps {
  aiEnabled: boolean;
  activeTab: DocumentDetailsViewOptions;
  theme: Theme;
}

/**
 * Calculates and manages the dynamic height of the document viewer.
 * Determines height based on viewport, viewer position, and active tab.
 * Updates on window resize and responds to tab changes (collapses when AI tabs are active).
 * Returns both the ref to attach to the viewer and the calculated height value.
 */
export function useViewerHeight({
  aiEnabled,
  activeTab,
  theme,
}: UseViewerHeightProps) {
  const viewerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState('100vh');

  useLayoutEffect(() => {
    const updateHeight = () => {
      if (aiEnabled && activeTab !== 'document') {
        setHeight('0px');
        return;
      }

      if (viewerRef.current) {
        const rect = viewerRef.current.getBoundingClientRect();
        const topOffset = rect.top;
        const bottomPaddingOffset = theme.spacing(2);
        const newHeight = `calc(100vh - ${topOffset}px - ${bottomPaddingOffset})`;
        setHeight(newHeight);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, [theme, activeTab, aiEnabled]);

  return {
    viewerRef,
    height,
  };
}
